import { defineStore } from 'pinia'
import { authApi } from '@modules/auth/services/api/authApi'
import { mapAuthError } from '@modules/auth/services/authErrorMessages'
import { TokenService } from '@modules/auth/services/TokenService'
import { getFingerprint } from '@modules/auth/services/FingerprintService'
import vuexStore from '@/store'
import type {
  CompanyListItem,
  CompanyListResponse,
  Permissions,
} from '@modules/auth/services/api/types'

interface AuthState {
  companyList: CompanyListResponse | null
  currentCompany: CompanyListItem | null
  permissions: Permissions | null
  loading: boolean
  error: string | null
  currentStep: string
  phone: string
  otpId: string | null
  testCode: string | null
}

interface InitOtpPayload {
  phone: string
  validateMethod: string
  deliveryMethod: string
}

interface LoginPayload {
  phone: string
  password: string
}

interface LoginCurrentCompanyPayload {
  companyId: string
}

interface ResetPasswordOtpPayload {
  phone: string
  validateMethod?: string
  deliveryMethod?: string
}

interface CompleteOtpResetPayload {
  otpId: string
  code: string
  deliveryMethod?: string
}

interface CompleteResetPayload {
  otpId: string
  code: string
  password: string
  deliveryMethod?: string
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    companyList: null,
    currentCompany: null,
    permissions: TokenService.getPermissions<Permissions>(),
    loading: false,
    error: null,
    currentStep: '1',
    phone: '',
    otpId: null,
    testCode: null,
  }),

  getters: {
    hasRight: (state) => (right: string): boolean => {
      if (state.permissions === null) return true
      return state.permissions.rights?.includes(right) ?? false
    },
  },

  actions: {
    setStep(step: string) { this.currentStep = step },
    setPhone(phone: string) { this.phone = phone },
    setError(error: string | null) { this.error = error },
    setOtpId(otpId: string | null) { this.otpId = otpId },
    setTestCode(testCode: string | null) { this.testCode = testCode },

    clearStepData(step?: string) {
      if (!step || step === '1') {
        this.phone = ''
        this.otpId = null
        this.testCode = null
        this.error = null
      }
      if (!step || step === '2') {
        this.error = null
      }
      if (!step || step === '3') {
        this.companyList = null
        this.currentCompany = null
      }
    },

    async initOtp(payload: InitOtpPayload) {
      this.loading = true
      this.error = null
      try {
        return await authApi.initOtp(payload)
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to send verification code')
        throw e
      } finally {
        this.loading = false
      }
    },

    async login(data: LoginPayload) {
      this.loading = true
      this.error = null
      try {
        await getFingerprint()
        const res = await authApi.login(data)
        TokenService.logout()
        TokenService.setUserId(res.data.userId)
        TokenService.setAccessToken(res.data.accessToken)
        TokenService.setRefreshToken(res.data.refreshToken, res.data.refreshTokenExpiredTo)
        this.permissions = res.data.permissions
        TokenService.setPermissions(this.permissions)
        // Sync permissions to Vuex so legacy portal-right checks (fetchAccounts, etc.)
        // work on the first navigation without needing a page refresh.
        vuexStore.commit('SET_PERMISSIONS', this.permissions)
        return res
      } catch (e: any) {
        this.error = mapAuthError(e, 'Login failed')
        throw e
      } finally {
        this.loading = false
      }
    },

    async getCompanyList() {
      this.loading = true
      try {
        const { data } = await authApi.getCompanies()
        this.companyList = data
        return data
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to fetch companies', { unauthorizedAs: 'session-expired' })
        throw e
      } finally {
        this.loading = false
      }
    },

    async loginCurrentCompany(data: LoginCurrentCompanyPayload) {
      this.loading = true
      this.error = null
      try {
        const res = await authApi.selectCompany(data)
        TokenService.setEntityId(data.companyId)
        TokenService.setAccessToken(res.data.accessToken)
        if (res.data.permissions) {
          this.permissions = res.data.permissions
          TokenService.setPermissions(this.permissions)
          vuexStore.commit('SET_PERMISSIONS', this.permissions)
        }
        return res
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to select company', { unauthorizedAs: 'session-expired' })
        throw e
      } finally {
        this.loading = false
      }
    },

    async createNewEntity() {
      this.loading = true
      this.error = null
      try {
        const { status, data } = await authApi.createEntity()
        if (status === 200 && data.entityId) {
          TokenService.setEntityId(data.entityId)
        }
        return data
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to create company', { unauthorizedAs: 'session-expired' })
        throw e
      } finally {
        this.loading = false
      }
    },

    async initOtpResetPassword(payload: ResetPasswordOtpPayload) {
      this.loading = true
      this.error = null
      try {
        return await authApi.initOtpResetPassword(payload)
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to send reset code')
        throw e
      } finally {
        this.loading = false
      }
    },

    async completeOtpResetPassword(payload: CompleteOtpResetPayload) {
      this.loading = true
      this.error = null
      try {
        return await authApi.completeOtpResetPassword(payload)
      } catch (e: any) {
        this.error = mapAuthError(e, 'Invalid verification code')
        throw e
      } finally {
        this.loading = false
      }
    },

    async completeResetPassword(payload: CompleteResetPayload) {
      this.loading = true
      this.error = null
      try {
        return await authApi.completeResetPassword(payload)
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to reset password')
        throw e
      } finally {
        this.loading = false
      }
    },
  },
})
